// FoodExplorer — the second-most-important screen in Kids Mode.
// A grid of big emoji food tiles the kid can scroll, tap to learn
// about, and request from a grown-up.
//
// Filter chips across the top toggle by card type (Super / Build /
// Boost / Spark). "All" shows everything.
//
// Tap a tile → FoodDetail sheet pops with a giant emoji, the food's
// name, a fun fact, and a big "I WANT THIS!" button that writes
// to kid_food_requests + fires a notification to the parent via
// the migration 0183 trigger.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis.

import { useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { KID_FOODS, CARD_TYPE_META, foodsByType, foodById } from "./kidFoods";
import { useKidRecipes } from "../../lib/useKidRecipes";
import { useKidFavorites } from "../../lib/useKidFavorites";
import { useRecipePhotos } from "../../lib/useRecipePhotos";
import { useDessertGate } from "../../lib/useDessertGate";
import { useKidVisiblePantryItems } from "../../lib/useKidVisiblePantryItems";
import { playKidSfx } from "../../lib/kidSfx";
import { playKidVoice } from "../../lib/kidVoice";
import { playCanonicalVoiceline, playRecipeVoiceline } from "../../lib/canonicalVoicelines";
import { useKidPetState } from "../../lib/useKidPetState";
import { canonicalImageUrlFor } from "../../lib/canonicalIcons";
import { MyAsksStrip } from "./MyAsksStrip";
import { useMyKidRequests } from "../../lib/useMyKidRequests";

const ALL_TYPES = ["super", "build", "boost", "spark"];

// Kid-facing copy for an active request's state, so a food the kid
// already asked for SHOWS its status instead of offering another ask.
const REQ_STATUS_META = {
  pending:  { emoji: "💤", label: "You asked for this!",            color: "#9C7A18", bg: "rgba(212,166,55,0.18)", border: "rgba(212,166,55,0.55)" },
  accepted: { emoji: "✋", label: "Yes! A grown-up is making it!",  color: "#3F6328", bg: "rgba(123,156,92,0.20)", border: "rgba(123,156,92,0.6)" },
  cooking:  { emoji: "🔥", label: "It's cooking right now!",        color: "#A34711", bg: "rgba(217,107,43,0.20)", border: "rgba(217,107,43,0.65)" },
};

// Tiny corner badge on a tile when the kid has an active ask for it.
function TileReqBadge({ state }) {
  const meta = REQ_STATUS_META[state];
  if (!meta) return null;
  return (
    <div style={{
      position: "absolute", top: 4, right: 4, zIndex: 2,
      width: 22, height: 22, borderRadius: 11,
      background: meta.bg, border: `1.5px solid ${meta.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 13, lineHeight: 1,
    }}>
      {meta.emoji}
    </div>
  );
}

// Full status pill shown inside a food detail in place of the ask
// button when an active request exists.
function RequestStatusPill({ state }) {
  const meta = REQ_STATUS_META[state];
  if (!meta) return null;
  return (
    <div style={{
      width: "100%", padding: "14px 16px", borderRadius: RADIUS.xxl,
      background: meta.bg, border: `2px solid ${meta.border}`,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 18,
      color: meta.color, lineHeight: 1.2,
    }}>
      <span style={{ fontSize: 24 }}>{meta.emoji}</span>
      {meta.label}
    </div>
  );
}

// Top-level segments. Recipes is the default landing because the
// kid's most common ask is "I want X for dinner/snack" against a
// parent-curated list (egg burgers, dino nuggets, chicken roll-ups,
// smoothies). Foods is the raw-ingredient cravings flow. Treats is
// dessert recipes gated behind the kid actually eating something
// recently (useDessertGate).
const SEGMENTS = [
  { key: "recipes", label: "Recipes", emoji: "🍽️" },
  { key: "foods",   label: "Foods",   emoji: "🥕" },
  { key: "treats",  label: "Treats",  emoji: "🍰" },
];

export function FoodExplorer({ userId, onWatch }) {
  const [segment, setSegment] = useState("recipes");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);   // canonical food
  const [selectedMeal, setSelectedMeal] = useState(null); // recipe row
  const [selectedPantry, setSelectedPantry] = useState(null); // pantry_items row
  // Current pet so we know which voice to play when the kid taps a
  // pantry tile. ensureKidPetState returns a row with pet_slug;
  // pet_catalog.voice_id resolves the ElevenLabs ID per species.
  const { pet } = useKidPetState(userId);

  // (Inline ingredient_info JOIN now lives inside
  // useKidVisiblePantryItems — no dbMap dependency, no
  // 24h-cache invalidation dance needed.)

  // Soft thunk SFX on every tile open so the kid hears the action.
  // Wrappers around the setters so we don't have to thread playKidSfx
  // through every tile/grid/onClick site. Pantry tiles ALSO play the
  // canonical voiceline matched to the kid's current pet's voice —
  // cat-pet kid hears the cat saying "Eggwich!", alien-pet kid
  // hears the alien. Silent fallback when no voiceline exists yet
  // (parent hasn't flipped kid_visible from MCMItemCard, or the
  // edge-fn generation hasn't finished).
  const openFood   = (f) => { playKidSfx("thunk"); setSelected(f); };
  const openMeal   = (m) => {
    playKidSfx("thunk");
    setSelectedMeal(m);
    // Recipe voiceline keyed on (target_kind='recipe', recipe_slug).
    // Generated when the parent flips ADD TO KIDS MENU on the
    // recipe page (RecipeKidMenuSection). Silent fallback when the
    // gen hasn't completed yet or no voice_id matches the pet.
    const slug = m?.recipe_slug || m?.slug;
    if (slug && pet?.pet_slug) {
      playRecipeVoiceline(slug, pet.pet_slug);
    }
  };
  const openPantry = (p) => {
    playKidSfx("thunk");
    setSelectedPantry(p);
    if (p?.canonical_id && pet?.pet_slug) {
      playCanonicalVoiceline(p.canonical_id, pet.pet_slug);
    }
  };
  const [recentRequest, setRecentRequest] = useState(null);

  // Kid-side: every recipe any family adult has marked. uniqueByKind
  // buckets by meal / snack / dessert (migration 0242) so the
  // segments above can render their own slice.
  const { uniqueBySlug: meals, uniqueByKind } = useKidRecipes(userId);
  const favs = useKidFavorites(userId);

  // Active requests the kid has out — so tiles + details SHOW their
  // status ("you asked", "a grown-up's making it", "cooking now")
  // instead of letting the kid ask again into the void.
  const { rows: myRequests } = useMyKidRequests(userId);
  const reqMaps = useMemo(() => {
    const byCanon = new Map();
    const bySlug  = new Map();
    for (const r of myRequests || []) {
      if (r.displayState === "celebrate") continue;   // done — not active
      if (r.canonical_id) byCanon.set(r.canonical_id, r.displayState);
      if (r.recipe_slug)  bySlug.set(r.recipe_slug, r.displayState);
    }
    return { byCanon, bySlug };
  }, [myRequests]);
  const foodReqState = (id)   => (id   ? reqMaps.byCanon.get(id)   : null) || null;
  const mealReqState = (slug) => (slug ? reqMaps.bySlug.get(slug)  : null) || null;
  const pantryReqState = (item) => foodReqState(item?.canonical_id || item?.canonicalId || item?.id);

  // Dessert gate — whether the kid is allowed to request anything
  // in the Treats segment right now. Derived from kid_food_requests
  // (non-dessert resolved in the last 4 hours).
  const dessertGate = useDessertGate(userId);

  // Pantry items the family has flagged as kid-requestable via the
  // KidPantryManager. Mirrors uniqueByKind shape so the Recipes /
  // Treats sections can render meal / snack / dessert pantry tiles
  // alongside parent-tagged recipes.
  const { buckets: pantryBuckets } = useKidVisiblePantryItems(userId);

  // "My Book" filter resolves each favorite back to its full record
  // (food or meal) so the existing tiles render. Sorted by favorite
  // recency so the most-recently-loved shows first.
  const favoriteFoods = useMemo(() => {
    return favs.favorites
      .filter((f) => f.item_kind === "canonical")
      .map((f) => foodById(f.item_id))
      .filter(Boolean);
  }, [favs.favorites]);
  const favoriteMeals = useMemo(() => {
    const bySlug = new Map(meals.map((m) => [m.recipe_slug, m]));
    return favs.favorites
      .filter((f) => f.item_kind === "recipe")
      .map((f) => bySlug.get(f.item_id) || {
        recipe_slug:  f.item_id,
        recipe_title: f.item_name,
        recipe_emoji: f.item_emoji,
        imageUrl:     null,
      });
  }, [favs.favorites, meals]);

  // Foods-segment sub-filter (My Book / All / Super / Build / Boost
  // / Spark). Only meaningful when segment === "foods".
  const isMyBook = filter === "myBook";
  const foods = isMyBook
    ? favoriteFoods
    : filter === "all"
      ? KID_FOODS
      : foodsByType(filter);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      {/* "My Asks" strip — sits ABOVE the segment control so the
          kid can always see the status of what they asked for
          regardless of which segment they're on. Tapping a
          "cooking" pill routes to the Watch tab via onWatch. */}
      <MyAsksStrip userId={userId} onWatch={onWatch} />
      {/* Top-level 3-segment control. Recipes is the default landing
          because that's where the parent-curated stuff lives — what
          the kid actually wants for a meal. Foods is the raw-
          ingredient grid for snack cravings. Treats is dessert
          recipes, locked behind the dessert gate. */}
      <SegmentControl
        segment={segment}
        onSegment={(s) => {
          if (s !== segment) playKidSfx("tick");
          setSegment(s);
          setFilter("all");
        }}
        treatsLocked={!dessertGate.unlocked}
        treatsCount={uniqueByKind.dessert.length + pantryBuckets.dessert.length}
      />

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          padding: "0 12px 32px",
        }}
      >
        {/* ─── RECIPES segment — parent-marked recipes by kid_kind.
              Meals first, snacks below as a separate section so the
              kid can tell "real food" from "afterschool snack". */}
        {segment === "recipes" && (
          <>
            {uniqueByKind.meal.length === 0
              && uniqueByKind.snack.length === 0
              && pantryBuckets.meal.length === 0
              && pantryBuckets.snack.length === 0
              && (
              <EmptyRecipesState />
            )}
            {pantryBuckets.meal.length > 0 && (
              <>
                <SectionHeader emoji="🥫" label="From your kitchen" />
                <PantryGrid items={pantryBuckets.meal} onSelect={openPantry} statusFor={pantryReqState} />
              </>
            )}
            {uniqueByKind.meal.length > 0 && (
              <>
                <SectionHeader emoji="🍽️" label="Meals from grown-ups" />
                <RecipeGrid meals={uniqueByKind.meal} onSelect={openMeal} statusFor={mealReqState} />
              </>
            )}
            {pantryBuckets.snack.length > 0 && (
              <>
                <SectionHeader emoji="🥨" label="Snacks from your kitchen" />
                <PantryGrid items={pantryBuckets.snack} onSelect={openPantry} statusFor={pantryReqState} />
              </>
            )}
            {uniqueByKind.snack.length > 0 && (
              <>
                <SectionHeader emoji="🥨" label="Snacks" />
                <RecipeGrid meals={uniqueByKind.snack} onSelect={openMeal} statusFor={mealReqState} />
              </>
            )}
            {favoriteMeals.length > 0 && (
              <>
                <SectionHeader emoji="💖" label="My favorites" />
                <RecipeGrid meals={favoriteMeals} onSelect={openMeal} statusFor={mealReqState} />
              </>
            )}
          </>
        )}

        {/* ─── FOODS segment — raw ingredients with the legacy
              card-type + My Book sub-filters. Same flow as before. */}
        {segment === "foods" && (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                padding: "4px 0 12px",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {favs.favorites.length > 0 && (
                <FilterChip
                  label="My Book"
                  emoji="💖"
                  color="#D96B2B"
                  active={filter === "myBook"}
                  onClick={() => setFilter("myBook")}
                />
              )}
              <FilterChip
                label="All"
                emoji="🥗"
                active={filter === "all"}
                onClick={() => setFilter("all")}
              />
              {ALL_TYPES.map((t) => (
                <FilterChip
                  key={t}
                  label={CARD_TYPE_META[t].label}
                  emoji={CARD_TYPE_META[t].emoji}
                  color={CARD_TYPE_META[t].color}
                  active={filter === t}
                  onClick={() => setFilter(t)}
                />
              ))}
            </div>
            {isMyBook && favs.favorites.length === 0 ? (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  fontFamily: FONT.forma,
                  fontSize: 14,
                  color: COLOR.muted,
                  lineHeight: 1.5,
                }}
              >
                <div style={{ fontSize: 60, lineHeight: 1, marginBottom: 12 }}>💖</div>
                Tap the heart on foods you like, and they'll show up here!
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                  gap: 10,
                }}
              >
                {foods.map((f) => (
                  <FoodTile key={f.id} food={f} onClick={() => openFood(f)} reqState={foodReqState(f.id)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── TREATS segment — dessert recipes. When locked, the
              tiles render dimmed + non-tappable with a "have real
              food first" message above. When unlocked, normal tap
              flow. */}
        {segment === "treats" && (
          <>
            {!dessertGate.unlocked && (
              <DessertLockedBanner />
            )}
            {uniqueByKind.dessert.length === 0 && pantryBuckets.dessert.length === 0 ? (
              <EmptyTreatsState locked={!dessertGate.unlocked} />
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                  gap: 10,
                  opacity: dessertGate.unlocked ? 1 : 0.45,
                  filter:  dessertGate.unlocked ? "none" : "grayscale(0.6)",
                  pointerEvents: dessertGate.unlocked ? "auto" : "none",
                  transition: "opacity 240ms ease, filter 240ms ease",
                }}
              >
                {pantryBuckets.dessert.map((p) => (
                  <PantryTile key={`p:${p.id}`} item={p} onClick={() => openPantry(p)} reqState={pantryReqState(p)} />
                ))}
                {uniqueByKind.dessert.map((m) => (
                  <MealTile key={m.recipe_slug} meal={m} onClick={() => openMeal(m)} reqState={mealReqState(m.recipe_slug)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <FoodDetail
          food={selected}
          userId={userId}
          favs={favs}
          requestState={foodReqState(selected.id)}
          onClose={() => setSelected(null)}
          onRequested={() => {
            playKidSfx("trill");
            // Pet voiceline confirms the ask landed. ~700ms after
            // the trill so the two audio events don't overlap.
            setTimeout(() => playKidVoice("asked-a-parent"), 700);
            setRecentRequest({ emoji: selected.emoji, label: "Asked a grown-up!" });
            setSelected(null);
            setTimeout(() => setRecentRequest(null), 2400);
          }}
        />
      )}

      {selectedMeal && (
        <MealDetail
          meal={selectedMeal}
          userId={userId}
          favs={favs}
          requestState={mealReqState(selectedMeal.recipe_slug)}
          onClose={() => setSelectedMeal(null)}
          onRequested={() => {
            playKidSfx("trill");
            // Pet voiceline confirms the ask landed. ~700ms after
            // the trill so the two audio events don't overlap.
            setTimeout(() => playKidVoice("asked-a-parent"), 700);
            setRecentRequest({ emoji: selectedMeal.recipe_emoji || "🍽️", label: "Asked for this meal!" });
            setSelectedMeal(null);
            setTimeout(() => setRecentRequest(null), 2400);
          }}
        />
      )}

      {selectedPantry && (
        <PantryItemDetail
          item={selectedPantry}
          userId={userId}
          requestState={foodReqState(selectedPantry.canonicalId || selectedPantry.canonical_id || selectedPantry.id)}
          onClose={() => setSelectedPantry(null)}
          onRequested={() => {
            playKidSfx("trill");
            // Pet voiceline confirms the ask landed. ~700ms after
            // the trill so the two audio events don't overlap.
            setTimeout(() => playKidVoice("asked-a-parent"), 700);
            setRecentRequest({
              emoji: selectedPantry.emoji || "🥫",
              label: "Asked for this item!",
            });
            setSelectedPantry(null);
            setTimeout(() => setRecentRequest(null), 2400);
          }}
        />
      )}

      {recentRequest && <RequestSentToast emoji={recentRequest.emoji} label={recentRequest.label} />}
    </div>
  );
}

function SegmentControl({ segment, onSegment, treatsLocked, treatsCount }) {
  return (
    <div
      role="tablist"
      aria-label="Foods sections"
      style={{
        display: "flex",
        gap: 6,
        padding: "6px 12px 12px",
        flexShrink: 0,
      }}
    >
      {SEGMENTS.map((s) => {
        const active = segment === s.key;
        const isTreats = s.key === "treats";
        const showLock = isTreats && treatsLocked;
        return (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSegment(s.key)}
            className="mcm-focusable"
            style={{
              flex: 1,
              padding: "10px 6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              background: active ? COLOR.leaf : "#fff",
              border: `2px solid ${active ? COLOR.leaf : COLOR.border}`,
              borderRadius: 14,
              cursor: "pointer",
              touchAction: "manipulation",
              boxShadow: active ? `0 4px 12px ${COLOR.leafDim || "rgba(46,125,50,0.25)"}` : "none",
              transition: "background 160ms ease, border-color 160ms ease",
              position: "relative",
            }}
          >
            <span style={{ fontSize: 26, lineHeight: 1, filter: showLock ? "grayscale(0.7)" : "none" }}>
              {s.emoji}
            </span>
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: active ? "#fff" : COLOR.ink,
                textTransform: "uppercase",
              }}
            >
              {s.label}
            </span>
            {showLock && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  top: 4, right: 6,
                  fontSize: 14,
                  filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.25))",
                }}
              >
                🔒
              </span>
            )}
            {isTreats && !showLock && treatsCount > 0 && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  top: 4, right: 6,
                  background: active ? "#fff" : COLOR.leaf,
                  color: active ? COLOR.leaf : "#fff",
                  fontSize: 10,
                  fontFamily: FONT.mono,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 8,
                }}
              >
                {treatsCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function RecipeGrid({ meals, onSelect, statusFor }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
        gap: 10,
        marginBottom: 16,
      }}
    >
      {meals.map((m) => (
        <MealTile key={m.recipe_slug} meal={m} onClick={() => onSelect(m)} reqState={statusFor?.(m.recipe_slug)} />
      ))}
    </div>
  );
}

function EmptyRecipesState() {
  return (
    <div
      style={{
        padding: "48px 20px",
        textAlign: "center",
        fontFamily: FONT.forma,
        fontSize: 14,
        color: COLOR.muted,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 12 }}>🍽️</div>
      Ask a grown-up to add your favorite foods here so you can request them!
    </div>
  );
}

function EmptyTreatsState({ locked }) {
  return (
    <div
      style={{
        padding: "48px 20px",
        textAlign: "center",
        fontFamily: FONT.forma,
        fontSize: 14,
        color: COLOR.muted,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 12 }}>{locked ? "🔒" : "🍰"}</div>
      No treats here yet — a grown-up can add some sweet recipes!
    </div>
  );
}

function DessertLockedBanner() {
  return (
    <div
      style={{
        margin: "0 0 14px",
        padding: "14px 16px",
        background: "linear-gradient(135deg, #FFEFD0 0%, #FFE0B0 100%)",
        border: `2px dashed #D4A637`,
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: FONT.forma,
        fontSize: 13,
        color: "#7A5A0F",
        lineHeight: 1.4,
      }}
    >
      <span style={{ fontSize: 30, flexShrink: 0 }}>🍴</span>
      <span>
        <strong>Real food first!</strong> Eat a meal and treats will unlock.
      </span>
    </div>
  );
}

function SectionHeader({ emoji, label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 4px 10px",
        fontFamily: FONT.mono,
        fontSize: 11,
        letterSpacing: "0.1em",
        fontWeight: 700,
        color: COLOR.sub,
      }}
    >
      <span style={{ fontSize: 16 }}>{emoji}</span>
      {label.toUpperCase()}
    </div>
  );
}

function MealTile({ meal, onClick, reqState }) {
  const hasPhoto = !!meal.imageUrl;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        background: hasPhoto
          ? `url(${meal.imageUrl}) center / cover no-repeat`
          : "linear-gradient(135deg, #FFF8E7 0%, #FAF6EE 100%)",
        border: `1.5px solid ${hasPhoto ? "rgba(255,255,255,0.45)" : COLOR.leafDim}`,
        borderRadius: RADIUS.xxl,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        aspectRatio: "1 / 1",
        position: "relative",
        boxShadow: hasPhoto
          ? "0 2px 8px rgba(30,30,30,0.18)"
          : "0 1px 4px rgba(30,30,30,0.06)",
        touchAction: "manipulation",
      }}
    >
      <TileReqBadge state={reqState} />
      {/* When there's a photo, render a soft gradient at the
          bottom so the title reads against any color. When there
          isn't, fall back to the big emoji centerpiece. */}
      {hasPhoto ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      ) : (
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -56%)",
            fontSize: 44,
            lineHeight: 1,
          }}
        >
          {meal.recipe_emoji || "🍽️"}
        </span>
      )}
      <span
        style={{
          position: "relative",
          fontFamily: FONT.forma,
          fontSize: 11,
          fontWeight: 700,
          color: hasPhoto ? "#fff" : COLOR.ink,
          textAlign: "center",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          padding: hasPhoto ? "8px 6px 8px" : "0 6px 8px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          textShadow: hasPhoto ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
        }}
      >
        {meal.displayName || meal.recipe_title}
      </span>
    </button>
  );
}

function MealDetail({ meal, userId, favs, onClose, onRequested, requestState }) {
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [photoToast, setPhotoToast] = useState(false);
  const favored = favs?.isFavorite("recipe", meal.recipe_slug) || false;
  const photos = useRecipePhotos(meal.recipe_slug, userId);
  const fileRef = useRef(null);

  const takePhoto = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await photos.uploadFromFile(file, { setAsPrimary: !photos.primary });
    e.target.value = "";
    if (url) {
      setPhotoToast(true);
      setTimeout(() => setPhotoToast(false), 1800);
    }
  };

  const request = async () => {
    if (!userId) return;
    setRequesting(true);
    setError(null);
    // food_name uses the kid-facing displayName when set (e.g.
    // "Egg Burgers"), so the parent inbox shows what the kid
    // actually asked for instead of the canonical recipe title.
    // request_kind="dessert" when the recipe was tagged that way so
    // the dessert-gate-aware logic stays correct (e.g. requesting a
    // dessert doesn't reset the dessert window).
    const kindTag = meal.kid_kind === "dessert" ? "dessert" : "recipe";
    const { error } = await supabase.from("kid_food_requests").insert({
      kid_user_id:  userId,
      canonical_id: meal.recipe_slug,   // legacy NOT NULL — slug doubles as the id
      recipe_slug:  meal.recipe_slug,
      request_kind: kindTag,
      food_emoji:   meal.recipe_emoji,
      food_name:    meal.displayName || meal.recipe_title,
    });
    setRequesting(false);
    if (error) {
      const msg = String(error.message || "");
      if (msg.includes("rate_limit_duplicate")) {
        setError("💤 You already asked for this — wait a bit!");
      } else if (msg.includes("rate_limit_daily")) {
        setError("🛑 You've asked for a lot today. Try tomorrow!");
      } else {
        // Surface the underlying server message in the console so the
        // next silent failure doesn't go black-hole the way the
        // request_kind='dessert' check-constraint violation did
        // before migration 0243.
        console.warn("[kid-food-requests] insert failed:", msg);
        setError("could not send right now");
      }
      return;
    }
    onRequested();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 230,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "24px 24px calc(28px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
          textAlign: "center",
        }}
      >
        {meal.imageUrl ? (
          <div
            style={{
              width: "100%",
              aspectRatio: "16 / 11",
              background: `url(${meal.imageUrl}) center / cover no-repeat`,
              borderRadius: 16,
              marginBottom: 14,
              boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
            }}
            aria-label={meal.recipe_title}
          />
        ) : (
          <div
            style={{
              fontSize: 120,
              lineHeight: 1,
              marginBottom: 8,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))",
            }}
          >
            {meal.recipe_emoji || "🍽️"}
          </div>
        )}

        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 30,
            color: COLOR.ink,
            marginBottom: 16,
            lineHeight: 1.2,
          }}
        >
          {meal.displayName || meal.recipe_title}
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: `${COLOR.leafDim}`,
            border: `1.5px solid ${COLOR.leaf}`,
            borderRadius: 999,
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 16 }}>👨‍🍳</span>
          <span
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: COLOR.leaf,
            }}
          >
            FAMILY MEAL
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <HeartButton
            favored={favored}
            onClick={() => favs?.toggle({
              kind:  "recipe",
              id:    meal.recipe_slug,
              name:  meal.recipe_title,
              emoji: meal.recipe_emoji,
            })}
          />
          <PhotoButton busy={photos.uploading} onClick={takePhoto} />
          {requestState ? (
            <div style={{ flex: 1 }}><RequestStatusPill state={requestState} /></div>
          ) : (
          <button
            type="button"
            onClick={request}
            disabled={requesting}
            style={{
              flex: 1,
              padding: "18px 16px",
              background: requesting ? COLOR.leafDim : COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 14,
              fontFamily: FONT.mono,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.1em",
              cursor: requesting ? "wait" : "pointer",
              boxShadow: requesting ? "none" : `0 6px 16px ${COLOR.leafDim}`,
              touchAction: "manipulation",
            }}
          >
            {requesting ? "ASKING…" : "I WANT THIS MEAL! 🙋"}
          </button>
          )}
        </div>

        {photoToast && (
          <div
            style={{
              padding: "10px 14px",
              background: COLOR.leafDeep,
              border: `1px solid ${COLOR.leafDim}`,
              borderRadius: 10,
              color: COLOR.leaf,
              fontFamily: FONT.forma,
              fontSize: 13,
              fontWeight: 600,
              textAlign: "center",
              marginBottom: 4,
            }}
          >
            📸 Photo saved!
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          style={{ display: "none" }}
        />

        {error && (
          <div
            style={{
              marginTop: 12,
              fontFamily: FONT.forma,
              fontSize: 13,
              color: COLOR.rose,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 14,
            padding: "10px 20px",
            background: "transparent",
            border: "none",
            color: COLOR.muted,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          BACK
        </button>
      </div>
    </div>
  );
}

function FilterChip({ label, emoji, color, active, onClick }) {
  const accent = color || COLOR.leaf;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: "unset",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        background: active ? accent : "rgba(255,255,255,0.7)",
        border: `2px solid ${active ? accent : COLOR.border}`,
        borderRadius: 999,
        whiteSpace: "nowrap",
        flexShrink: 0,
        boxShadow: active ? `0 2px 8px ${accent}44` : "none",
        transition: "background 120ms ease, border-color 120ms ease",
        touchAction: "manipulation",
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>
      <span
        style={{
          fontFamily: FONT.forma,
          fontSize: 14,
          fontWeight: 700,
          color: active ? "#fff" : COLOR.ink,
        }}
      >
        {label}
      </span>
    </button>
  );
}

function FoodTile({ food, onClick, reqState }) {
  const meta = CARD_TYPE_META[food.cardType];
  return (
    <button
      type="button"
      onClick={onClick}
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: RADIUS.xxl,
        padding: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        aspectRatio: "1 / 1",
        position: "relative",
        boxShadow: "0 1px 4px rgba(30,30,30,0.05)",
        touchAction: "manipulation",
      }}
    >
      <TileReqBadge state={reqState} />
      <span style={{ fontSize: 44, lineHeight: 1, marginBottom: 4 }}>{food.emoji}</span>
      <span
        style={{
          fontFamily: FONT.forma,
          fontSize: 12,
          fontWeight: 600,
          color: COLOR.ink,
          textAlign: "center",
          lineHeight: 1.2,
          opacity: 0.85,
        }}
      >
        {food.displayName}
      </span>
      {/* Tiny card-type dot in the corner — color-coded so the kid
          starts to learn which foods give which power-up. */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: meta.color,
        }}
      />
    </button>
  );
}

function FoodDetail({ food, userId, favs, onClose, onRequested, requestState }) {
  const meta = CARD_TYPE_META[food.cardType];
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const favored = favs?.isFavorite("canonical", food.id) || false;

  const request = async () => {
    if (!userId) return;
    setRequesting(true);
    setError(null);
    const { error } = await supabase.from("kid_food_requests").insert({
      kid_user_id:  userId,
      canonical_id: food.id,
      food_emoji:   food.emoji,
      food_name:    food.displayName,
    });
    setRequesting(false);
    if (error) {
      const msg = String(error.message || "");
      if (msg.includes("rate_limit_duplicate")) {
        setError("💤 You already asked for this — wait a bit!");
      } else if (msg.includes("rate_limit_daily")) {
        setError("🛑 You've asked for a lot today. Try tomorrow!");
      } else {
        // Surface the underlying server message in the console so the
        // next silent failure doesn't go black-hole the way the
        // request_kind='dessert' check-constraint violation did
        // before migration 0243.
        console.warn("[kid-food-requests] insert failed:", msg);
        setError("could not send right now");
      }
      return;
    }
    onRequested();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 230,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "24px 24px calc(28px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 120,
            lineHeight: 1,
            marginBottom: 8,
            filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))",
          }}
        >
          {food.emoji}
        </div>

        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 32,
            color: COLOR.ink,
            marginBottom: 6,
          }}
        >
          {food.displayName}
        </div>

        {/* Card-type chip — teaches the kid what power-up this food gives. */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: `${meta.color}22`,
            border: `1.5px solid ${meta.color}`,
            borderRadius: 999,
            marginBottom: 14,
          }}
        >
          <span style={{ fontSize: 16 }}>{meta.emoji}</span>
          <span
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: meta.color,
            }}
          >
            {meta.label.toUpperCase()} CARD
          </span>
        </div>

        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 15,
            color: COLOR.muted,
            lineHeight: 1.5,
            marginBottom: 24,
            maxWidth: 340,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {food.funFact}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <HeartButton
            favored={favored}
            onClick={() => favs?.toggle({
              kind:  "canonical",
              id:    food.id,
              name:  food.displayName,
              emoji: food.emoji,
            })}
          />
          {requestState ? (
            <div style={{ flex: 1 }}><RequestStatusPill state={requestState} /></div>
          ) : (
          <button
            type="button"
            onClick={request}
            disabled={requesting}
            style={{
              flex: 1,
              padding: "18px 16px",
              background: requesting ? COLOR.leafDim : COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 14,
              fontFamily: FONT.mono,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.1em",
              cursor: requesting ? "wait" : "pointer",
              boxShadow: requesting ? "none" : `0 6px 16px ${COLOR.leafDim}`,
              touchAction: "manipulation",
            }}
          >
            {requesting ? "ASKING…" : "I WANT THIS! 🙋"}
          </button>
          )}
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              fontFamily: FONT.forma,
              fontSize: 13,
              color: COLOR.rose,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 14,
            padding: "10px 20px",
            background: "transparent",
            border: "none",
            color: COLOR.muted,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          BACK
        </button>
      </div>
    </div>
  );
}

function PhotoButton({ busy, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label="take a photo"
      className="mcm-focusable"
      style={{
        flexShrink: 0,
        width: 64,
        height: 64,
        padding: 0,
        background: busy ? COLOR.skyDeep : "#fff",
        border: `2px solid ${COLOR.skyBorder}`,
        borderRadius: 14,
        fontSize: 32,
        cursor: busy ? "wait" : "pointer",
        touchAction: "manipulation",
        boxShadow: busy ? "none" : "0 2px 6px rgba(30,30,30,0.06)",
        transition: "background 200ms ease",
      }}
    >
      {busy ? "…" : "📸"}
    </button>
  );
}

function HeartButton({ favored, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={favored ? "remove from My Book" : "save to My Book"}
      className="mcm-focusable"
      style={{
        flexShrink: 0,
        width: 64,
        height: 64,
        padding: 0,
        background: favored ? "rgba(217,107,43,0.16)" : "#fff",
        border: `2px solid ${favored ? COLOR.rose : COLOR.border}`,
        borderRadius: 14,
        fontSize: 32,
        cursor: "pointer",
        touchAction: "manipulation",
        boxShadow: favored ? `0 4px 14px rgba(217,107,43,0.25)` : "none",
        transition: "background 200ms ease, border-color 200ms ease, transform 200ms ease",
        transform: favored ? "scale(1.04)" : "scale(1)",
      }}
    >
      {favored ? "💖" : "🤍"}
    </button>
  );
}

function RequestSentToast({ emoji, label }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "calc(140px + env(safe-area-inset-bottom))",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 220,
        background: COLOR.leaf,
        color: "#fff",
        padding: "12px 18px",
        borderRadius: 999,
        fontFamily: FONT.forma,
        fontSize: 14,
        fontWeight: 600,
        boxShadow: "0 8px 20px rgba(47,143,131,0.35)",
        animation: "kid-toast-in 200ms ease-out",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ fontSize: 22 }}>{emoji}</span>
      <span>{label}</span>
      <style>{`
        @keyframes kid-toast-in {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PANTRY-ITEM rendering — a parent-tagged pantry_items row shown as
// a kid-requestable tile. Distinct from MealTile (recipe) because the
// data shape is different and we want a visual cue ("from your
// kitchen" via a subtle pantry-jar background) that tells the kid
// they're asking for a specific product they've seen at home.

function PantryGrid({ items, onSelect, statusFor }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
        gap: 10,
        marginBottom: 16,
      }}
    >
      {items.map((item) => (
        <PantryTile key={item.id} item={item} onClick={() => onSelect(item)} reqState={statusFor?.(item)} />
      ))}
    </div>
  );
}

function PantryTile({ item, onClick, reqState }) {
  const display = item.displayName || item.name || "Item";
  // Icon URL is stamped onto the row by useKidVisiblePantryItems'
  // inline JOIN against ingredient_info. Bundled SVG/JPG (for
  // hand-curated canonicals like eggwich) wins over the AI-
  // generated URL via canonicalImageUrlFor's first-tier check,
  // synthesizing a synthetic info shape so the resolver can see
  // both tiers.
  const iconUrl = item.canonical_id
    ? canonicalImageUrlFor(item.canonical_id, { imageUrl: item.icon_url })
    : null;
  const hasPhoto = !!iconUrl;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        // Distinct plum tint so the kid + parent can tell at a glance
        // "this is a specific kitchen item" vs a recipe tile.
        background: hasPhoto
          ? `url(${iconUrl}) center / contain no-repeat #FFFFFF`
          : "linear-gradient(135deg, #F2E9FA 0%, #FAF6EE 100%)",
        border: `1.5px solid ${hasPhoto ? "rgba(255,255,255,0.45)" : "#9B5DE555"}`,
        borderRadius: RADIUS.xxl,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        aspectRatio: "1 / 1",
        position: "relative",
        boxShadow: hasPhoto
          ? "0 2px 8px rgba(30,30,30,0.18)"
          : "0 1px 4px rgba(30,30,30,0.06)",
        touchAction: "manipulation",
      }}
    >
      <TileReqBadge state={reqState} />
      {!hasPhoto && (
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -56%)",
            fontSize: 44,
            lineHeight: 1,
          }}
        >
          {item.emoji || "🥫"}
        </span>
      )}
      <span
        style={{
          position: "relative",
          fontFamily: FONT.forma,
          fontSize: 11,
          fontWeight: 700,
          color: hasPhoto ? "#fff" : COLOR.ink,
          textShadow: hasPhoto ? "0 1px 3px rgba(0,0,0,0.55)" : "none",
          background: hasPhoto
            ? "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)"
            : "transparent",
          textAlign: "center",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          padding: "12px 6px 8px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {display}
      </span>
    </button>
  );
}

function PantryItemDetail({ item, userId, onClose, onRequested, requestState }) {
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const display = item.displayName || item.name || "Item";

  const request = async () => {
    if (!userId) return;
    setRequesting(true);
    setError(null);
    // kind === 'dessert' uses the dessert request_kind so the gate
    // logic stays consistent (resolving a dessert pantry-ask doesn't
    // reset the dessert window). Otherwise 'pantry' so the inbox can
    // render the FROM KITCHEN pill and link back to the row.
    const kindTag = item.kid_kind === "dessert" ? "dessert" : "pantry";
    const { error: e } = await supabase.from("kid_food_requests").insert({
      kid_user_id:    userId,
      canonical_id:   item.canonical_id || item.id,
      request_kind:   kindTag,
      food_emoji:     item.emoji || null,
      food_name:      display,
      pantry_item_id: item.id,
    });
    setRequesting(false);
    if (e) {
      const msg = String(e.message || "");
      if (msg.includes("rate_limit_duplicate")) {
        setError("💤 You already asked for this — wait a bit!");
      } else if (msg.includes("rate_limit_daily")) {
        setError("🛑 You've asked for a lot today. Try tomorrow!");
      } else {
        console.warn("[kid-food-requests] pantry insert failed:", msg);
        setError("could not send right now");
      }
      return;
    }
    onRequested();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 230,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "24px 24px calc(28px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
          textAlign: "center",
        }}
      >
        {(() => {
          // Match the tile resolver so the detail-sheet hero shows the
          // same family icon (bundled SVG/JPG → AI-generated PNG →
          // canonical emoji fallback) the kid just tapped on. Before
          // this the tiles were accurate but tapping in collapsed
          // back to the canonical-emoji fallback.
          const detailIconUrl = item.canonical_id
            ? canonicalImageUrlFor(item.canonical_id, { imageUrl: item.icon_url })
            : null;
          return detailIconUrl ? (
            <div
              role="img"
              aria-label={display}
              style={{
                width: 160, height: 160,
                margin: "0 auto 8px",
                background: `url(${detailIconUrl}) center / contain no-repeat #FFFFFF`,
                borderRadius: 28,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.10))",
              }}
            />
          ) : (
            <div
              style={{
                fontSize: 120,
                lineHeight: 1,
                marginBottom: 8,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))",
              }}
            >
              {item.emoji || "🥫"}
            </div>
          );
        })()}
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 30,
            color: COLOR.ink,
            marginBottom: 8,
            lineHeight: 1.2,
          }}
        >
          {display}
        </div>
        {item.brand && (
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              color: COLOR.muted,
              letterSpacing: "0.08em",
              marginBottom: 16,
            }}
          >
            {item.brand.toUpperCase()}
          </div>
        )}

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#F2E9FA",
            border: `1.5px solid #9B5DE5`,
            borderRadius: 999,
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 16 }}>🥫</span>
          <span
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "#7A4E2D",
            }}
          >
            FROM YOUR KITCHEN
          </span>
        </div>

        {requestState ? (
          <RequestStatusPill state={requestState} />
        ) : (
        <button
          type="button"
          onClick={request}
          disabled={requesting}
          style={{
            width: "100%",
            padding: "16px 18px",
            background: requesting ? COLOR.leafDim : COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: 14,
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 20,
            cursor: requesting ? "wait" : "pointer",
            boxShadow: requesting ? "none" : `0 6px 14px ${COLOR.leafDim}`,
            touchAction: "manipulation",
          }}
        >
          {requesting ? "Asking…" : "I WANT THIS!"}
        </button>
        )}

        {error && (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 10,
            padding: 12,
            background: "transparent",
            color: COLOR.muted,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          NEVER MIND
        </button>
      </div>
    </div>
  );
}

export default FoodExplorer;
